/**
 * Created by Daniel Wang on 6/1/2022.
 * Description:
 * ------ maintenance history ------
 */
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Subject, Subscription, of } from 'rxjs';
import { AppState } from '../../../../redux';
import { createSelector, select, Store } from '@ngrx/store';

import { NotificationOptions, NotificationStyles } from '../../../../widgets/notification/notification.model';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';
import { businessConstants } from '../../../../constants/business.constants';
import { AdminPageActionTypes, UpdateDefaultSidenoteType } from '../../../../redux/actions/admin-page.actions';
import { catchError, debounceTime, filter, take } from 'rxjs/operators';
import { lastCustomActionSelector } from '../../../../redux/reducers/action-history.reducer';
import { userSettingSelector } from '../../../../redux/reducers/users.reducer';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { AdminPageActionsComponent } from '../../admin-page-actions/admin-page-actions.component';
import { UntypedFormControl } from '@angular/forms';
import { ConvertAddressType, GeneralGridType } from '../../../../tamalelibs/models/admin-page.model';
import { adminGeneralPageSelector } from '../../../../redux/reducers/admin-page.reducer';
import { EntityBrief } from '../../../../tamalelibs/models/entity-brief.model';
import { EntityService } from '../../../../tamalelibs/services/entity.service';
import { BriefConfig } from '../../../../widgets/widgets.model';
import { AdminPagesService } from '../../../../tamalelibs/services/admin-page.service';
import { AdminPageFeedback, AdminPageFeedbackTypes } from '../../admin-page-setting.model';
import { SlideSheetService } from '../../../../services/slide-sheet.service';
import { SlideSheetActionTypes } from '../../../slide-sheet/slide-sheet.model';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { ActionHistoryActionTypes } from '../../../../redux/actions/action-history.actions';
import { AdminPageWidgetComponent } from '../admin-page-widget/admin-page-widget.component';
import * as _lodash from 'lodash';
import { SystemActionTypes } from '../../../../tamalelibs/redux/actions/system.actions';

@Component({
    selector: 'tam-admin-general',
    templateUrl: './admin-general.component.html',
    styleUrls: ['./admin-general.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminGeneralComponent implements OnInit, OnDestroy {
    //#region  public properties
    @ViewChild('entityList', { static: false }) entityList;

    briefConfig = new BriefConfig();
    buttonTitle: string;
    config: Array<any> = [];
    columnDefs: Array<ColDef>;
    feedbackSubject$ = new Subject<AdminPageFeedback>();
    gridOptions: GridOptions;
    isAutoConversion = true;
    isProgressing = false;
    isPublicAuthorization = true;
    isShowCreateItemButton = false;
    entityDatasource: Array<EntityBrief>;
    entityItem: EntityBrief;
    name = new UntypedFormControl('');
    originalEntity: EntityBrief;
    rowData: Array<any> = [];
    selectedIndex = 0;
    selectedItem = businessConstants.note.defaultSidenoteType.firstNote;
    tooltip = StringLiteralsPipe.translate('note.defaulting_note_type_of_a_sidenote_tooltip');
    typeItems = [businessConstants.note.defaultSidenoteType.firstNote, businessConstants.note.defaultSidenoteType.latestNote];
    //#endregion

    //#region private properties
    private _destroySubscriptions: Array<Subscription> = [];
    private _gridApi: GridApi;
    //#endregion

    //#region constructor
    constructor(
        private _adminPageService: AdminPagesService,
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _entityService: EntityService,
        private _slideSheetService: SlideSheetService,
        private _store: Store<AppState>,
        private _toastService: ToastService
    ) {
    }
    //#endregion

    //#region public
    ngOnInit() {
        this._initAGGridProperties();
        this._initAGGridData();
        this._initSubscriptions();
        this._initControlValue();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    handleEntityFilter(value) {
        this.briefConfig.actionSubject$.next({
            type: 'FilterEntity',
            payload: value
        });
    }

    onConvertAllContactsAddress() {
        this._updateConvertAddress(ConvertAddressType.Contacts);
    }

    onConvertAllCorporatesAddress() {
        this._updateConvertAddress(ConvertAddressType.Corporates);
    }

    onCreateItemClick() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.ADMIN_CONSOLE_GENERAL,
            payload: {
                type: this._getPageTypeByIndex(this.selectedIndex)
            }
        });
    }

    onGridReady(params) {
        this._gridApi = params.api;
        setTimeout(() => {
            this._gridApi.sizeColumnsToFit();
        });
    }

    onOpenEntity(event: any): void {
        if (this.entityItem && this.entityItem.name) {
            this._searchingEntity(this.entityItem.name);
        } else {
            this._searchingEntity('');
        }
    }

    onSaveDraftNotEntityClick() {
        this._adminPageService.updateDraftNoteEntity(this.entityItem.id).pipe(
            take(1),
            catchError(error => {
                return of(null);
            })
        ).subscribe(res => {
            if (res) {
                this.originalEntity = _lodash.cloneDeep(this.entityItem);
                const notice = new NotificationOptions();
                notice.style = NotificationStyles.Success;
                notice.message = StringLiteralsPipe.translate('general.saved_successfully');
                this._toastService.notify(notice);
                this._store.dispatch({
                    type: SystemActionTypes.GET_UNCATEGORUZEEID
                });
            }
        });
    }

    onSaveEntryClick() {
        this.isProgressing = true;
        const type = this.selectedItem;
        this._store.dispatch(new UpdateDefaultSidenoteType(type));
    }

    onTabSelectChange(index) {
        if (index !== this.selectedIndex) {
            this.entityItem = this.originalEntity;
            this.selectedIndex = index;
            this._setButtonStatus();
            this._loadAGGridRowData(this.selectedIndex);
        }
    }

    selectionChange(event: any): void {
        if (!event?.isPublic) {
            const notice = new NotificationOptions();
            notice.style = NotificationStyles.Warning;
            notice.message = StringLiteralsPipe.translate('general.draft_note_private_entity_info');
            this._toastService.notify(notice);
        }
    }

    updateAutoConversion(event) {
        this._adminPageService.updateAutoConversion(event).pipe(
            take(1),
            catchError(error => {
                return of(null);
            })
        ).subscribe(res => {
            if (res) {
                const notice = new NotificationOptions();
                if (res?.message.toLowerCase() === 'ok') {
                    notice.style = NotificationStyles.Success;
                    notice.message = StringLiteralsPipe.translate('general.update_successfully');
                } else {
                    notice.style = NotificationStyles.Error;
                    notice.message = res.message;
                }
                this._toastService.notify(notice);
            }
        });
    }
    //#endregion

    //#region private functions
    private _changeFMRStatus(event) {
        if (this.selectedIndex === 5) {
            const data = event.payload.data;
            if (data) {
                this._store.dispatch({
                    type: AdminPageActionTypes.EDIT_GENERAL_PAGE,
                    payload: {
                        type: this._getPageTypeByIndex(this.selectedIndex),
                        item: data
                    }
                });
            }
        }
    }

    private _closeSlideSheet() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE
        });
    }

    private _initAGGridColumns(type: GeneralGridType) {
        switch (type) {
            case GeneralGridType.ConfigureHTMLSecurity:
                this.columnDefs = [{
                    headerName: 'Name',
                    field: 'tag',
                    colId: 'tag',
                    menuTabs: [],
                    comparator: (a, b) => {
                        if (typeof a === 'string') {
                            return a.localeCompare(b);
                        } else {
                            return (a > b ? 1 : (a < b ? -1 : 0));
                        }
                    },
                }, {
                    headerName: 'Edit/Delete',
                    minWidth: 105,
                    width: 105,
                    maxWidth: 105,
                    cellRenderer: AdminPageActionsComponent,
                    cellRendererParams: { feedbackSubject$: () => this.feedbackSubject$ },
                    sortable: false,
                    suppressMovable: true,
                    cellStyle: { 'text-align': 'center' },
                }];
                break;
            case GeneralGridType.Abbreviation:
                this.columnDefs = [{
                    headerName: 'Abbreviation',
                    field: 'abbreviation',
                    colId: 'abbreviation',
                    menuTabs: [],
                    comparator: (a, b) => {
                        if (typeof a === 'string') {
                            return a.localeCompare(b);
                        } else {
                            return (a > b ? 1 : (a < b ? -1 : 0));
                        }
                    },
                }, {
                    headerName: 'Canonical Form',
                    field: 'canonicalForm',
                    colId: 'canonicalForm',
                    menuTabs: []
                }, {
                    headerName: 'Edit/Delete',
                    minWidth: 105,
                    width: 105,
                    maxWidth: 105,
                    cellRenderer: AdminPageActionsComponent,
                    cellRendererParams: { feedbackSubject$: () => this.feedbackSubject$ },
                    sortable: false,
                    suppressMovable: true,
                    cellStyle: { 'text-align': 'center' },
                }];
                break;
            case GeneralGridType.FMROverrides:
                this.columnDefs = [{
                    headerName: 'Name',
                    field: 'name',
                    colId: 'name',
                    menuTabs: [],
                    comparator: (a, b) => {
                        if (typeof a === 'string') {
                            return a.localeCompare(b);
                        } else {
                            return (a > b ? 1 : (a < b ? -1 : 0));
                        }
                    },
                }, {
                    headerName: 'FMR',
                    field: 'FMR',
                    colId: 'FMR',
                    cellRenderer: AdminPageWidgetComponent,
                    cellRendererParams: {
                        feedbackSubject$: () => this.feedbackSubject$,
                        feedbackType: 'FMR',
                        type: 'check-box'
                    },
                    menuTabs: [],
                }, {
                    headerName: 'Delete',
                    minWidth: 105,
                    width: 105,
                    maxWidth: 105,
                    cellRenderer: AdminPageActionsComponent,
                    cellRendererParams: { feedbackSubject$: () => this.feedbackSubject$ },
                    sortable: false,
                    suppressMovable: true,
                    cellStyle: { 'text-align': 'center' },
                }];
                break;
            case GeneralGridType.StopList:
                this.columnDefs = [{
                    headerName: 'Ticker',
                    field: 'ticker',
                    colId: 'ticker',
                    menuTabs: [],
                    comparator: (a, b) => {
                        if (typeof a === 'string') {
                            return a.localeCompare(b);
                        } else {
                            return (a > b ? 1 : (a < b ? -1 : 0));
                        }
                    },
                }, {
                    headerName: 'Delete',
                    minWidth: 105,
                    width: 105,
                    maxWidth: 105,
                    cellRenderer: AdminPageActionsComponent,
                    cellRendererParams: { feedbackSubject$: () => this.feedbackSubject$ },
                    sortable: false,
                    suppressMovable: true,
                    resizable: false,
                    cellStyle: { 'text-align': 'center' },
                }];
                break;
            default:
                break;
        }
        return this.columnDefs;
    }

    private _initAGGridData() {
        this.config = [{
            id: GeneralGridType.ConfigureHTMLSecurity,
            name: 'Configure HTML Security',
            columnDefs: this._initAGGridColumns(GeneralGridType.ConfigureHTMLSecurity),
            rowData: this._loadAGGridRowData(0), // 0 means no data is loaded
            index: 3
        }, {
            id: GeneralGridType.Abbreviation,
            name: 'Abbreviation',
            columnDefs: this._initAGGridColumns(GeneralGridType.Abbreviation),
            rowData: this._loadAGGridRowData(0), // 0 means no data is loaded
            index: 4
        }, {
            id: GeneralGridType.FMROverrides,
            name: 'FMR Override',
            columnDefs: this._initAGGridColumns(GeneralGridType.FMROverrides),
            rowData: this._loadAGGridRowData(0), // 0 means no data is loaded
            index: 5
        }, {
            id: GeneralGridType.StopList,
            name: 'Stop List',
            columnDefs: this._initAGGridColumns(GeneralGridType.StopList),
            rowData: this._loadAGGridRowData(0), // 0 means no data is loaded
            index: 6
        }];
    }

    private _initAGGridProperties() {
        this.gridOptions = {
            defaultColDef: {
                sortable: true,
                resizable: true,
                menuTabs: []
            },
            cacheBlockSize: 50,
            suppressCellFocus: true,
            enableBrowserTooltips: true,
            rowSelection: 'single',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns: true,
        };
    }

    private _initControlValue() {
        this._setButtonStatus();

        this._adminPageService.getDraftNoteEntity().pipe(
            take(1),
            catchError(error => {
                return of(null);
            })
        ).subscribe(res => {
            if (res) {
                const entityBrief = new EntityBrief(res.id, res.name);
                entityBrief.shortName = res.name;
                this.entityItem = entityBrief;
                this.originalEntity = _lodash.cloneDeep(this.entityItem);
                this._searchingEntity(res.name);
            }
        });

        this._adminPageService.getConvertAddress().pipe(
            take(1),
            catchError(error => {
                return of(null);
            })
        ).subscribe(res => {
            if (res) {
                this.isAutoConversion = JSON.parse(res.autoConversion);
            }
        });
    }

    private _initSubscriptions() {
        this._destroySubscriptions.push(
            this._store.pipe(
                select(createSelector(state => state[businessConstants.globalConfig.systemConfig], (state) => state ? state[businessConstants.globalConfig.entryConfig] : null)),
                filter(state => !!state)
            ).subscribe(this._onSystemConfigChange.bind(this)),

            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => {
                this._onSubscribeActionMessage(action);
            }),

            this._store.pipe(
                select(userSettingSelector),
                filter(items => items && items.length !== 0)
            ).subscribe(userSetting => {
                this._onSubscribeUserSetting(userSetting);
            }),

            this._store.pipe(
                select(adminGeneralPageSelector)
            ).subscribe(data => {
                if (data) {
                    if (Array.isArray(data)) {
                        const pageType = this._getPageTypeByIndex(this.selectedIndex);
                        if (pageType) {
                            this.config.forEach(item => {
                                if (item.id === pageType) {
                                    item.rowData = data;
                                }
                            });
                            this._changeDetectorRef.detectChanges();
                        }
                    }
                }
            }),

            this.briefConfig.actionSubject$.pipe(
                debounceTime(250),
                filter(res => res != null && res.payload.length > 0)
            ).subscribe(res => {
                if (res.type === 'FilterEntity') {
                    this._searchingEntity(res.payload);
                }
            }),

            this.feedbackSubject$.subscribe(feedback => this._handleGridActionFeedback(feedback)),
        );
    }

    private _getPageTypeByIndex(index: number) {
        let pageType = '';
        if (index === 3) {
            pageType = GeneralGridType.ConfigureHTMLSecurity;
        } else if (index === 4) {
            pageType = GeneralGridType.Abbreviation;
        } else if (index === 5) {
            pageType = GeneralGridType.FMROverrides;
        } else if (index === 6) {
            pageType = GeneralGridType.StopList;
        }
        return pageType;
    }

    private _handleDelete(feedback: AdminPageFeedback) {
        const item = feedback?.payload?.data;
        let name = '';
        if (this.selectedIndex === 3) {
            name = item['tag'];
        } else if (this.selectedIndex === 4) {
            name = item['abbreviation'];
        } else if (this.selectedIndex === 5) {
            name = item['name'];
        } else if (this.selectedIndex === 6) {
            name = item['ticker'];
        }
        const message = `Are you sure you want to delete ${name}?`;
        const subscription = this._alertWindow.warn(StringLiteralsPipe.translate('general.delete_confirmation_required'),
            [message], 'Delete', 'Cancel')
            .subscribe((result: boolean) => {
                if (result) {
                    this._store.dispatch({
                        type: AdminPageActionTypes.DELETE_GENERAL_PAGE,
                        payload: {
                            type: this._getPageTypeByIndex(this.selectedIndex),
                            id: item.id ? item.id : item.ID,
                            name: name
                        }
                    });
                }
                subscription.unsubscribe();
            });
    }

    private _handleEdit(feedback: AdminPageFeedback) {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.ADMIN_CONSOLE_GENERAL,
            payload: {
                type: this._getPageTypeByIndex(this.selectedIndex),
                data: feedback.payload.data
            }
        });
    }

    private _handleGridActionFeedback(feedback) {
        switch (feedback.type) {
            case AdminPageFeedbackTypes.EDIT:
                this._handleEdit(feedback);
                break;
            case AdminPageFeedbackTypes.DELETE:
                this._handleDelete(feedback);
                break;
            case AdminPageFeedbackTypes.TOGGLE_WEB:
                this._changeFMRStatus(feedback);
                break;
            default:
                break;
        }
    }

    private _loadAGGridRowData(index: number) {
        let pageType = '';
        pageType = this._getPageTypeByIndex(index);
        if (pageType !== '') {
            this._store.dispatch({
                type: AdminPageActionTypes.GET_GENERAL_PAGE,
                payload: {
                    type: pageType
                }
            });
        }
    }

    private _onSystemConfigChange(res) {
        const sideNoteFollowRule = res[businessConstants.note.sideNoteFollowRule];
        if (sideNoteFollowRule === businessConstants.note.defaultSidenoteType.firstNoteId ||
            sideNoteFollowRule === businessConstants.note.defaultSidenoteType.firstNote) {
            this.selectedItem = businessConstants.note.defaultSidenoteType.firstNote;
        } else {
            this.selectedItem = businessConstants.note.defaultSidenoteType.latestNote;
        }
    }

    private _onSubscribeActionMessage(action) {
        if (action.type === AdminPageActionTypes.UPDATE_DEFAULT_SIDENOTE_TYPE_SUCCESS) {
            const notice = new NotificationOptions();
            notice.style = NotificationStyles.Success;
            notice.message = StringLiteralsPipe.translate('general.saved_successfully');
            this._toastService.notify(notice);
            this.isProgressing = false;
        } else if (action.type === AdminPageActionTypes.UPDATE_DEFAULT_SIDENOTE_TYPE_FAILED) {
            this.isProgressing = false;
        } else if (action.type === AdminPageActionTypes.GET_GENERAL_PAGE_SUCCESS) {
            const pageType = this._getPageTypeByIndex(this.selectedIndex);
            if (pageType) {
                this.config.forEach(item => {
                    if (item.id === pageType) {
                        item.rowData = action.payload;
                    }
                });
            }
        } else if (action.type === AdminPageActionTypes.CREATE_GENERAL_PAGE_SUCCESS) {
            let messageInfo = '';
            if (action.payload.type === GeneralGridType.Abbreviation) {
                messageInfo = 'Abbreviation ' + action.payload.data['abbreviation'] + ' is created successfully.';
            } else if (action.payload.type === GeneralGridType.FMROverrides) {
                messageInfo = 'FMR Override ' + action.payload.data['name'] + ' is created successfully.';
            } else if (action.payload.type === GeneralGridType.StopList) {
                messageInfo = 'Stop ticker ' + action.payload.data['ticker'] + ' is created successfully.';
            } else if (action.payload.type === GeneralGridType.ConfigureHTMLSecurity) {
                messageInfo = 'Tag ' + action.payload.data['tag'] + ' is created successfully.';
            }
            this._toastService.notify({
                style: NotificationStyles.Success,
                message: messageInfo
            });
            this._closeSlideSheet();
            this._removeTriggeredItemInActionHistory(action.type);
        } else if (action.type === AdminPageActionTypes.CREATE_GENERAL_PAGE_FAILURE) {
            this._toastService.notify({
                style: NotificationStyles.Error,
                message: action.payload
            });
            this._removeTriggeredItemInActionHistory(action.type);
        } else if (action.type === AdminPageActionTypes.EDIT_GENERAL_PAGE_SUCCESS) {
            let messageInfo = '';
            if (action.payload.type === GeneralGridType.Abbreviation) {
                messageInfo = 'Abbreviation ' + action.payload.data['abbreviation'] + ' is updated successfully.';
            } else if (action.payload.type === GeneralGridType.ConfigureHTMLSecurity) {
                messageInfo = 'Tag ' + action.payload.oldData['tag'] + ' is updated successfully.';
            } else if (action.payload.type === GeneralGridType.FMROverrides) {
                messageInfo = 'FMROverride ' + action.payload.data['name'] + ' is updated successfully.';
            }
            this._toastService.notify({
                style: NotificationStyles.Success,
                message: messageInfo
            });
            this._closeSlideSheet();
            this._removeTriggeredItemInActionHistory(action.type);
        } else if (action.type === AdminPageActionTypes.DELETE_GENERAL_PAGE_SUCCESS) {
            let messageInfo = '';
            if (action.payload.type === GeneralGridType.Abbreviation) {
                messageInfo = 'Abbreviation ' + action.payload.data.name + ' is deleted successfully.';
            } else if (action.payload.type === GeneralGridType.FMROverrides) {
                messageInfo = 'FMR Override ' + action.payload.data['name'] + ' is deleted successfully.';
            } else if (action.payload.type === GeneralGridType.StopList) {
                messageInfo = 'Stop ticker ' + action.payload.data['name'] + ' is deleted successfully.';
            } else if (action.payload.type === GeneralGridType.ConfigureHTMLSecurity) {
                messageInfo = 'Tag ' + action.payload.data['tag'] + ' is deleted successfully.';
            }
            this._toastService.notify({
                style: NotificationStyles.Success,
                message: messageInfo
            });
            this._removeTriggeredItemInActionHistory(action.type);
        }
        setTimeout(() => {
            this._changeDetectorRef.detectChanges();
        });
    }

    private _removeTriggeredItemInActionHistory(actionType) {
        this._store.dispatch({
            type: ActionHistoryActionTypes.REMOVE_ITEM,
            payload: {
                type: actionType
            }
        });
    }

    private _onSubscribeUserSetting(userSetting) {
        this.isPublicAuthorization = true;
        if (userSetting && userSetting.relationshipManage) {
            this.isPublicAuthorization = userSetting.relationshipManage.configuration.public;
        }
        this._changeDetectorRef.detectChanges();
    }

    private _searchingEntity(filterStr: string) {
        this._destroySubscriptions.push(
            this._entityService.getEntityListBySearchTextQuick(1, 20, filterStr).subscribe(res => {
                if (res) {
                    this.entityDatasource = this._entityService.mapEntityList(res).entities;
                }
            }));
    }

    private _setButtonStatus() {
        if (this.selectedIndex === 3) {
            this.buttonTitle = 'Create Html Security Tag';
            this.isShowCreateItemButton = true;
        } else if (this.selectedIndex === 4) {
            this.buttonTitle = 'Create Abbreviation';
            this.isShowCreateItemButton = true;
        } else if (this.selectedIndex === 5) {
            this.buttonTitle = 'Create Override';
            this.isShowCreateItemButton = true;
        } else if (this.selectedIndex === 6) {
            this.buttonTitle = 'Create Stop Ticker';
            this.isShowCreateItemButton = true;
        } else {
            this.isShowCreateItemButton = false;
        }
    }

    private _updateConvertAddress(type: ConvertAddressType) {
        this._adminPageService.updateConvertAddress(type).pipe(
            take(1),
            catchError(error => {
                return of(null);
            })
        ).subscribe(res => {
            if (res) {
                const notice = new NotificationOptions();
                if (res?.message.toLowerCase() === 'ok') {
                    notice.style = NotificationStyles.Success;
                    notice.message = StringLiteralsPipe.translate('general.convert_successfully');
                } else {
                    notice.style = NotificationStyles.Error;
                    notice.message = res.message;
                }
                this._toastService.notify(notice);
            }
        });
    }
    //#endregion
}
